// react
import { useMemo } from 'react';
import styled from 'styled-components';

// components
import { Accordion, Button, Title } from '@/components/atoms';
import { BackButton } from '@/components/molecules';
import { Editor } from '@/components/organisms/Editor/Editor';

/**
 * RecipientProfileAbout shows the profile of the care recipient.
 */
export const RecipientProfileAbout = ({
  className,
  name,
  keyInformationForm,
  nextOfKinForm,
  professionalContactsForm,
  answers,
  setAnswers,
  onSave,
  isEditing,
  isModify = false,
}: RecipientProfileAboutProps) => {
  // memos
  const SaveButton = useMemo(() => {
    if (!isModify) {
      return null;
    }
    return (
      <StyledButton
        variant={isEditing ? 'primary' : 'secondary'}
        onPress={onSave}
        ariaLabel={isEditing ? 'save' : 'edit'}
      >
        {isEditing ? 'Save' : 'Edit'}
      </StyledButton>
    );
  }, [isEditing, onSave, isModify]);

  return (
    <Wrapper className={className}>
      <BackButton />
      <Title>Key Information</Title>
      <SupportText>
        {`${name || 'Your loved one'}'s`} key information for you to share with
        others in the support network when needed.
      </SupportText>
      <Accordion
        items={[
          {
            id: 'key-information',
            label: 'Key Information',
            content: keyInformationForm && (
              <>
                {SaveButton}
                <Editor
                  fields={keyInformationForm}
                  isActionsDisabled={true}
                  isFloatingActions={false}
                  value={answers}
                  isDisabled={!isEditing}
                  onChange={(val, key) =>
                    setAnswers?.({ ...answers, [key]: val[key] })
                  }
                />
              </>
            ),
          },
          {
            id: 'next-of-kin',
            label: 'Next of Kin',
            content: nextOfKinForm && (
              <>
                {SaveButton}
                <Editor
                  fields={nextOfKinForm}
                  isActionsDisabled={true}
                  isFloatingActions={false}
                  value={answers}
                  isDisabled={!isEditing}
                  onChange={(val, key) =>
                    setAnswers?.({ ...answers, [key]: val[key] })
                  }
                />
              </>
            ),
          },
          {
            id: 'professional-contacts',
            label: 'Professional Contacts',
            content: professionalContactsForm && (
              <>
                {SaveButton}
                <Editor
                  fields={professionalContactsForm}
                  isActionsDisabled={true}
                  isFloatingActions={false}
                  value={answers}
                  isDisabled={!isEditing}
                  onChange={(val, key) =>
                    setAnswers?.({ ...answers, [key]: val[key] })
                  }
                />
              </>
            ),
          },
        ]}
      />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  padding-bottom: ${(props) => props.theme.spacing[24]};
`;

const SupportText = styled.div`
  color: ${(props) => props.theme.color.neutral[80]};
  font: ${(props) => props.theme.typography.body.md.regular};
`;

const StyledButton = styled(Button)`
  margin-top: ${(props) => props.theme.spacing[24]};
  margin-left: auto;
`;

interface RecipientProfileAboutProps {
  className?: string;
  name: string;
  keyInformationForm?: any;
  nextOfKinForm?: any;
  professionalContactsForm?: any;
  answers?: any;
  setAnswers?: (value: any) => void;
  careRecipient?: string;
  onSave?: () => void;
  isEditing?: boolean;
  isModify?: boolean;
}

RecipientProfileAbout.displayName = 'RecipientProfileAbout';

export default RecipientProfileAbout;
